import { buttonStyle } from 'components/buttons/buttonStyle';
import {
    CircleAlert,
    CircleCheck,
    DoneRounded as DoneRoundedIcon,
    Info,
    PriorityHighRounded as PriorityHighRoundedIcon,
    TriangleAlert,
} from 'components/icons';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Alert, AlertAction, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { AddNewInvoiceItems } from 'pages/invoices/hooks/useAddNewInvoiceItems';
import { useInvoiceDetails } from 'pages/invoices/hooks/useInvoiceDetails';
import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage';
import { getInvoiceTabAlerts } from 'pages/invoices/util/invoiceTabAlerts';
import { ComponentProps, Dispatch, ReactNode, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import BulkDiscountControl, { useBulkDiscountGate } from './BulkDiscountControl';
import InvoiceMatterDetails from './invoice-matters/InvoiceMatterDetails';
import { useInvoiceAlertNodes } from './InvoiceAlerts';
import { InvoiceSummaryRow } from './InvoiceSummaryRow';

type AlertTone = 'success' | 'warning' | 'error' | 'info';

const ALERT_TONE: Record<AlertTone, { className: string; icon: ReactNode }> = {
    success: { className: 'border-success bg-success-light', icon: <DoneRoundedIcon className="text-primary" /> },
    warning: {
        className: 'border-warning bg-warning-light',
        icon: <PriorityHighRoundedIcon className="text-warning" />,
    },
    error: { className: 'border-error bg-error-light', icon: <PriorityHighRoundedIcon className="text-error" /> },
    info: { className: 'border-info bg-info-lighter', icon: <PriorityHighRoundedIcon className="text-info" /> },
};

/**
 * The new design's tones: the kit Alert in the tone's tint, its glyph, and the tone's word for a screen reader, which
 * the colour and glyph say to the eye. Notices and done ones are statuses; only errors and warnings interrupt.
 */
const NEW_ALERT_TONE: Record<AlertTone, { className: string; icon: ReactNode; labelId: string; role: string }> = {
    success: {
        className: 'bg-success-tint text-success-ink',
        icon: <CircleCheck aria-hidden />,
        labelId: 'done',
        role: 'status',
    },
    warning: {
        className: 'bg-warning-tint text-warning-ink',
        icon: <TriangleAlert aria-hidden />,
        labelId: 'warning',
        role: 'alert',
    },
    error: {
        className: 'bg-danger-tint text-danger-ink',
        icon: <CircleAlert aria-hidden />,
        labelId: 'invoices.alerts.tone-error',
        role: 'alert',
    },
    info: { className: 'bg-info/8 text-foreground', icon: <Info aria-hidden />, labelId: 'note', role: 'status' },
};

/**
 * A filled invoice alert, tinted by its tone, with its action pushed to the right edge. In the new design its action
 * goes under the text in a narrow card, and a `description` follows the title; classic draws the title alone.
 */
export const DetailsAlert = ({
    tone,
    action,
    children,
    description,
}: {
    tone: AlertTone;
    action?: ReactNode;
    children: ReactNode;
    description?: ReactNode;
}) => {
    const isNewDesign = useIsNewDesign();
    if (isNewDesign) {
        const { className, icon, labelId, role } = NEW_ALERT_TONE[tone];
        return (
            <Alert
                role={role}
                className={cn(
                    'items-start border-transparent has-data-[slot=alert-action]:pr-4 sm:has-data-[slot=alert-action]:pr-36',
                    className
                )}
            >
                {icon}
                <AlertTitle>
                    <span className="sr-only">
                        <FormattedMessage id={labelId} />:{' '}
                    </span>
                    {children}
                </AlertTitle>
                {description ? <AlertDescription className="text-current">{description}</AlertDescription> : null}
                {action ? (
                    <AlertAction className="static col-start-2 mt-2 sm:absolute sm:mt-0">{action}</AlertAction>
                ) : null}
            </Alert>
        );
    }
    return (
        <ClassicDetailsAlert tone={tone} action={action}>
            {children}
        </ClassicDetailsAlert>
    );
};

const ClassicDetailsAlert = ({
    tone,
    action,
    children,
}: {
    tone: AlertTone;
    action?: ReactNode;
    children: ReactNode;
}) => (
    <Alert
        className={cn(
            ALERT_TONE[tone].className,
            'text-fg flex items-center gap-2 rounded-md py-2 pr-4 pl-3 text-sm leading-[1.125rem] font-semibold'
        )}
    >
        <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">{ALERT_TONE[tone].icon}</span>
        <div className="min-w-0">{children}</div>
        {action ? <div className="-mr-2 ml-auto flex items-start">{action}</div> : null}
    </Alert>
);

/** The white outlined button in an alert's action slot: the kit's small outline button in the new design. */
export const AlertActionButton = (props: Omit<ComponentProps<typeof Button>, 'className' | 'variant' | 'size'>) => (
    <Button
        {...buttonStyle(
            { variant: 'outlined', size: 'extraSmall' },
            cn(
                'bg-paper hover:bg-paper h-auto px-3 py-1 text-xs leading-[1.75] font-semibold',
                'new:bg-background new:hover:bg-muted new:h-8 new:px-2.5 new:py-0 new:text-sm new:leading-normal new:font-medium new:shadow-xs'
            )
        )}
        {...props}
    />
);

interface Props {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    addedInvoiceTimecardIds: number[];
    addedInvoiceExpenseIds: number[];
    setAddedInvoiceTimecardIds: Dispatch<SetStateAction<number[]>>;
    setAddedInvoiceExpenseIds: Dispatch<SetStateAction<number[]>>;
    successMessages: DetailsTabSuccessMessages;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    isLoading: boolean;
    handleStatusLoadingState: (isLoading: boolean) => void;
    addNewInvoiceItems: AddNewInvoiceItems;
}

const Details = ({
    invoice,
    classifiers,
    isInViewMode,
    addedInvoiceTimecardIds,
    addedInvoiceExpenseIds,
    setAddedInvoiceTimecardIds,
    setAddedInvoiceExpenseIds,
    successMessages,
    setSuccessMessages,
    isLoading,
    handleStatusLoadingState,
    addNewInvoiceItems,
}: Props) => {
    const { isAddingNewTimecards, isAddingNewExpenses } = addNewInvoiceItems;
    const { nodes: alertNodes, isUpdatingTimecardsHourlyRates } = useInvoiceAlertNodes({
        invoice,
        addNewInvoiceItems,
        setSuccessMessages,
        addedTimecardCount: addedInvoiceTimecardIds.length,
        addedExpenseCount: addedInvoiceExpenseIds.length,
    });

    const isSplitInvoice = invoice.isSplit ?? false;

    const {
        hasUninvoicedTimecards,
        hasUninvoicedExpenses,
        isTimecardEmployeeMissingRole,
        isTimecardMissingHourlyRate,
    } = useInvoiceDetails(invoice);
    const isJointInvoice = invoice.invoiceMatters && invoice.invoiceMatters.length > 1;
    const { isDisabled: isBulkDiscountControlDisabled, key: bulkDiscountKey } = useBulkDiscountGate(invoice);

    // The classic tab shows every item alert above the cases, in the source's order; the case's own stays on its card.
    const alerts = getInvoiceTabAlerts({
        invoice,
        isDetailsReadOnly: isInViewMode,
        hasUninvoicedTimecards,
        hasUninvoicedExpenses,
        isTimecardEmployeeMissingRole,
        isTimecardMissingHourlyRate,
        successMessages,
    }).flatMap((alert) => (alertNodes[alert.id] ? [alertNodes[alert.id]] : []));

    return (
        <>
            <InvoiceSummaryRow invoice={invoice} />
            {alerts.length ? <div className="flex flex-col gap-2">{alerts}</div> : <></>}
            {!isInViewMode && isJointInvoice ? (
                <BulkDiscountControl key={bulkDiscountKey} invoice={invoice} disabled={isBulkDiscountControlDisabled} />
            ) : null}
            {(invoice.invoiceMatters || [])
                .toSorted((a, b) =>
                    (a.matterNumber ?? '').localeCompare(b.matterNumber ?? '', undefined, { numeric: true })
                )
                .map((invoiceMatter, index) => {
                    const defaultExpanded =
                        !isSplitInvoice && invoice.invoiceMatters?.some((item) => item.hasMatterCapError)
                            ? invoiceMatter.hasMatterCapError
                            : index === 0;
                    return (
                        <InvoiceMatterDetails
                            defaultExpanded={defaultExpanded}
                            key={`invoiceMatter-${invoiceMatter.matterId ?? index}`}
                            invoiceMatter={invoiceMatter}
                            invoice={invoice}
                            classifiers={classifiers}
                            isInViewMode={isInViewMode}
                            handleStatusLoadingState={handleStatusLoadingState}
                            addedInvoiceTimecardIds={addedInvoiceTimecardIds}
                            setAddedInvoiceTimecardIds={setAddedInvoiceTimecardIds}
                            setSuccessMessages={setSuccessMessages}
                            successMessages={successMessages}
                            addedInvoiceExpenseIds={addedInvoiceExpenseIds}
                            setAddedInvoiceExpenseIds={setAddedInvoiceExpenseIds}
                            isAddingNewTimecards={isAddingNewTimecards}
                            isAddingNewExpenses={isAddingNewExpenses}
                            isUpdatingTimecardsHourlyRates={isUpdatingTimecardsHourlyRates}
                            isLoading={isLoading}
                        />
                    );
                })}
        </>
    );
};

export default Details;
